import { Controller } from "@hotwired/stimulus"

// FD-208: a Tiptap block editor that progressively enhances a hidden Action Text
// field. It hydrates from the field's current HTML and writes sanitized HTML back
// on every change, so the surrounding form submits exactly as the old Trix
// rich_text_area did — storage stays Action Text, only the input widget changes.
//
// FD-483: Tiptap/ProseMirror/Yjs (~1.5 MB source) load through a dynamic import
// of lib/block_editor_kit, which esbuild --splitting emits as an on-demand chunk.
// connect() is therefore async; every entry point that touches this.editor
// guards against the not-yet-loaded window.

// FD-598: the editor accepts images plus PDF and Excel. The picker `accept` and the
// paste/drop filter are conveniences only — Uploads::Image (Marcel-sniffed allowlist +
// 25 MB cap + capability gate) is the authoritative server-side gate. Excel is frequently
// declared application/octet-stream (or empty) by the OS/browser, so the filter also
// matches by extension so a dropped .xlsx isn't discarded before it reaches the server.
const ALLOWED_UPLOAD_ACCEPT = "image/*,application/pdf,.xls,.xlsx"
const ALLOWED_NON_IMAGE_TYPES = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
])
const ALLOWED_UPLOAD_EXTENSIONS = /\.(pdf|xls|xlsx)$/i

// What the collab banner says (see renderBanner).
const SAVE_ERROR_MESSAGE =
  "⚠ Changes aren't saving right now — keep this page open; we'll retry as you type."
const LIVE_EDITING_STOPPED_MESSAGE =
  "Live editing stopped for this page. Reload the page to see other people's edits."

// FD-1380: a live refresh re-renders every compose-and-append composer (the comment box, a
// reply box) from the server, where it is empty. Morphing it strips the mounted ProseMirror,
// and the FD-552 re-mount then seeds an empty editor from the empty hidden field: a
// half-written comment is gone and focus drops to <body>. So a composer the reader is in
// (focused) or has typed into is kept out of the morph, whole, the way the FD-568 guard keeps
// an open menu out. The rest of the feed still updates. An untouched composer is morphed as
// before. Only reset-on-submit composers: the description editors have their own guards.
// Installed once per page, because turbo:before-morph-element fires for every element.
let composerGuardInstalled = false

function installComposerGuard() {
  if (composerGuardInstalled) return
  composerGuardInstalled = true
  document.addEventListener("turbo:before-morph-element", (event) => {
    const el = event.target
    if (!(el instanceof HTMLElement) || !el.matches(".fd-block-editor[data-block-editor-reset-on-submit-value='true']")) return
    const surface = el.querySelector(".ProseMirror")
    if (!surface) return
    if (el.contains(document.activeElement) || surface.textContent.trim() !== "") {
      event.preventDefault()
    }
  })
}

export default class extends Controller {
  static targets = ["editor", "input", "banner"]
  static values = {
    placeholder: { type: String, default: "Write something…" },
    uploadUrl: { type: String, default: "" },
    // FD-621: JSON endpoint backing the "@" mention picker. Blank on surfaces with no
    // workspace context, which leaves the extension inert rather than erroring.
    mentionsUrl: { type: String, default: "" },
    // FD-438: clear the editor after a successful Turbo submit. Set on
    // compose-and-append composers (comments/replies) so a posted comment
    // doesn't leave its text behind for the next one; left false for
    // edit-in-place fields (description, page/wiki body) which keep content.
    resetOnSubmit: { type: Boolean, default: false },
    // FD-267: present only on a persisted page/wiki edit → collaborative mode.
    collabDocId: { type: String, default: "" },
    collabDocType: { type: String, default: "" },
    userName: { type: String, default: "" },
    userColor: { type: String, default: "" },
    autosaveUrl: { type: String, default: "" },
    autosaveParam: { type: String, default: "" },
    autosaveField: { type: String, default: "" },
  }

  async connect() {
    installComposerGuard()
    // FD-1093: draw the banner from this controller's own state before anything else. A
    // page Turbo restores from its cache brings back the banner as it was when the page
    // was left (a live-editing-stopped banner included), but the controller that connects
    // to it has no such state, and its editor subscribes afresh. A save error the save
    // path finds later still shows: it sets its own state and draws the banner again.
    this.renderBanner()
    // FD-483: guard the await — if this element disconnects (or Turbo re-connects
    // it) while the editor chunk is still downloading, the stale connect must not
    // mount an editor on a dead element.
    const token = (this.connectToken = {})
    const kit = await import("../lib/block_editor_kit")
    if (this.connectToken !== token || !this.element.isConnected) return

    this.kit = kit // FD-552: retained so a morph-stripped surface can be re-mounted.
    const {
      Editor, Y, Collaboration, CollaborationCaret, ActionCableYProvider,
    } = kit

    this.collab = this.collabDocIdValue !== "" && this.autosaveUrlValue !== ""

    const extensions = this.buildExtensions()

    if (this.collab) {
      // FD-267: a shared Y.Doc bound to this editor; the provider syncs it over Action
      // Cable. The doc is seeded from the stored HTML by exactly one elected client.
      this.ydoc = new Y.Doc()
      this.provider = new ActionCableYProvider({
        docType: this.collabDocTypeValue,
        docId: this.collabDocIdValue,
        doc: this.ydoc,
        onSeed: () => this.seedFromInput(),
        onLiveEditingStopped: () => this.showLiveEditingStopped(),
        onLiveEditingResumed: () => this.clearLiveEditingStopped(),
      })
      extensions.push(
        Collaboration.configure({ document: this.ydoc }),
        CollaborationCaret.configure({
          provider: this.provider,
          user: { name: this.userNameValue || "Someone", color: this.userColorValue || "#3b7dd8" },
        }),
      )
    }

    // FD-210: reveal the table-ops toolbar group only while the caret is in a table.
    this.toolbar = this.element.querySelector(".fd-block-toolbar")
    this.editor = this.mountEditor(extensions)
    // Non-collab: normalize the seed once so an unedited submit re-saves equivalent
    // HTML and an empty doc submits "" (the "page is empty" fallback). In collab the
    // hidden input keeps the server-rendered HTML until the shared doc loads, so the
    // form Save never races ahead of sync with an empty body.
    if (!this.collab) this.sync(this.editor)
    // FD-209: also sync on submit so the field always carries the latest editor
    // HTML, independent of onUpdate timing (e.g. a slash-menu insertion followed
    // immediately by Save).
    this.form = this.element.closest("form")
    this.syncOnSubmit = () => { if (this.editor) this.sync(this.editor) }
    this.form?.addEventListener("submit", this.syncOnSubmit)
    // A form reset (inline-edit's Cancel) restores form CONTROLS. The ProseMirror document
    // is not one, so the cancelled text stayed in the editor and was written back on the
    // next sync (submit or edit), silently saving and revisioning a discarded draft — and
    // the hidden input is no safety net either, being in "default mode" (see mountEditor).
    // Re-seed from the HTML the editor was mounted with. Collab surfaces are driven by the
    // shared Y.Doc, so (like the FD-552 re-mount) they're left untouched.
    this.onFormReset = () => this.resetToDefault()
    this.form?.addEventListener("reset", this.onFormReset)
    // FD-438: after a successful Turbo submit, clear compose-and-append composers
    // (comments/replies) so leftover text isn't carried into the next comment. The
    // turbo_stream response appends the new comment but leaves this form mounted, so
    // we reset the editor ourselves. Gated on resetOnSubmit; edit-in-place fields opt out.
    if (this.resetOnSubmitValue) {
      this.resetOnSubmitEnd = (event) => { if (event.detail?.success) this.resetComposer() }
      this.form?.addEventListener("turbo:submit-end", this.resetOnSubmitEnd)
    }
    // FD-552: a Turbo morph (FD-238 live refresh) that lands while this editor is idle
    // re-renders the surface as the server's EMPTY <div data-block-editor-target> and
    // idiomorph strips our client-mounted ProseMirror. Stimulus keeps this controller
    // (the element is preserved → no reconnect), so the editor would be silently dead —
    // clicking "edit" then shows a blank surface (the FD-552 peek symptom). Re-mount
    // from the freshly-morphed hidden input when that happens.
    this.onDocMorph = () => this.remountIfStripped()
    document.addEventListener("turbo:morph", this.onDocMorph)
    // FD-1186: a turbo-frame with refresh="morph" (the work-item activity feed, which holds
    // the comment composer) is reloaded AFTER the page morph and morphs its own children,
    // stripping the surface the same way. That render announces itself with
    // turbo:frame-render, never turbo:morph, so listen for both.
    document.addEventListener("turbo:frame-render", this.onDocMorph)
  }

  // FD-552: build the base (non-collab) extension set. Extracted so a morph re-mount
  // can rebuild it (ProseMirror extension instances aren't shared across editors).
  buildExtensions() {
    const {
      StarterKit, Image, Placeholder, TaskList, TaskItem, TableKit,
      SlashCommand, MentionSuggestion, Callout, ActionTextAttachment, PastedImages,
    } = this.kit
    return [
      // StarterKit v3 already bundles link + underline; configure its link
      // rather than adding a second (duplicate) Link extension. In collab mode the
      // built-in undo/redo is disabled — Yjs (via Collaboration) provides shared undo.
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: { openOnClick: false },
        ...(this.collab ? { undoRedo: false } : {}),
      }),
      Image,
      ActionTextAttachment,
      // FD-526: external pasted/dropped <img> policy — data URIs go through the
      // FD-211 upload endpoint, remote srcs are stripped keeping alt text.
      PastedImages.configure({
        upload: (file) => this.fetchUpload(file),
        onFailure: (count) => this.pastedImagesFailed(count),
      }),
      TaskList,
      TaskItem.configure({ nested: true }),
      TableKit.configure({ resizable: true }), // FD-210: tables
      Callout, // FD-210: callout block
      SlashCommand, // FD-209: "/" menu inserting blocks
      // FD-621: "@" mention picker. Registered here (not in connect) so the FD-552 morph
      // re-mount rebuilds it along with everything else. Adds plugins only — no schema
      // node — so the collab extensions pushed after this are unaffected.
      MentionSuggestion.configure({ url: this.mentionsUrlValue }),
      Placeholder.configure({ placeholder: this.placeholderValue }),
    ]
  }

  // FD-552: construct the Tiptap editor + wire toolbar refresh. Shared by connect and
  // the morph re-mount so both produce an identically-configured surface.
  mountEditor(extensions) {
    const { Editor } = this.kit
    // The HTML the editor is seeded from: the stored record exactly as the server rendered
    // it, and the baseline a form reset (inline-edit's Cancel) restores. The hidden input
    // can't serve as that baseline — <input type=hidden> is in "default mode", so every
    // sync() writes its value ATTRIBUTE too and defaultValue tracks the draft. The FD-552
    // re-mount re-reads it here, so a morph that refreshes the field moves the baseline with it.
    if (!this.collab) this.pristineHtml = this.inputTarget.value || ""
    const editor = new Editor({
      element: this.editorTarget,
      extensions,
      // Collaboration owns the content in collab mode (passing `content` would double
      // it); the doc is filled by sync/seed instead.
      ...(this.collab ? {} : { content: this.inputTarget.value || "" }),
      editorProps: {
        attributes: { class: "fd-prose fd-block-content" },
        handlePaste: (_view, event) => this.handleFiles(event.clipboardData && event.clipboardData.files, event),
        handleDrop: (_view, event) => this.handleFiles(event.dataTransfer && event.dataTransfer.files, event),
      },
      onUpdate: ({ editor }) => {
        this.sync(editor)
        if (this.collab) {
          this.scheduleAutosave()
          this.scheduleSnapshot()
        }
      },
    })
    editor.on("selectionUpdate", () => this.refreshToolbar())
    editor.on("transaction", () => this.refreshToolbar())
    this.refreshToolbar()
    return editor
  }

  // FD-552: if a morph stripped our mounted ProseMirror, re-mount on the now-empty
  // surface, seeding from the freshly server-rendered hidden input so the description
  // isn't lost. Collab surfaces are driven by the shared Y.Doc (not the morph path),
  // so they're left untouched.
  remountIfStripped() {
    if (!this.editor || this.collab) return
    if (this.editorTarget.querySelector(".ProseMirror")) return // surface survived the morph
    this.editor.destroy()
    this.editor = this.mountEditor(this.buildExtensions())
    this.sync(this.editor)
  }

  // FD-267: the elected client seeds the empty shared doc from the stored HTML. Guarded
  // so a late/duplicate election can't double-insert into an already-populated doc.
  seedFromInput() {
    const html = this.inputTarget.value
    if (!html || !this.editor) return
    if (this.ydoc.getXmlFragment("default").length > 0) return
    this.editor.commands.setContent(html)
  }

  scheduleAutosave() {
    clearTimeout(this._autosaveTimer)
    this._autosaveTimer = setTimeout(() => this.autosave(), 1500)
  }

  async autosave() {
    const payload = { autosave: true }
    payload[this.autosaveParamValue] = { [this.autosaveFieldValue]: this.inputTarget.value }
    // Keep one autosave in flight: abort any prior request so a delayed older save can't
    // commit stale HTML after a newer one (out-of-order writes on the single source of truth).
    this._autosaveAbort?.abort()
    this._autosaveAbort = new AbortController()
    try {
      const res = await fetch(this.autosaveUrlValue, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrfToken() },
        body: JSON.stringify(payload),
        signal: this._autosaveAbort.signal,
      })
      // `redirected` catches the expired-session case (302 → login page → 200, which
      // response.ok alone would miss); a 403/422 fails the .ok check. On any of these the
      // stored HTML — the system of record for collab pages — is NOT updated, so warn.
      if (!res.ok || res.redirected) this.showSaveError()
      else this.clearSaveError()
    } catch (error) {
      // Aborted ⇒ superseded by a newer save; ignore. Otherwise transient (offline /
      // reconnect) — surface it so silent loss is visible; the next edit reschedules a save.
      if (error.name !== "AbortError") this.showSaveError()
    }
  }

  showSaveError() {
    this.saveFailing = true
    this.renderBanner()
  }

  clearSaveError() {
    this.saveFailing = false
    this.renderBanner()
  }

  // FD-1093: the cable connection is gone for good (see ActionCableYProvider), so this tab
  // no longer sends or receives live edits. Kept until live editing comes back (a reload,
  // or the cable reopening, below): a later successful autosave clears a save error, not
  // this.
  showLiveEditingStopped() {
    this.liveEditingStopped = true
    this.renderBanner()
  }

  // FD-1093: the editor's subscription is confirmed again, so live edits flow again.
  clearLiveEditingStopped() {
    this.liveEditingStopped = false
    this.renderBanner()
  }

  // One banner, so it shows the most pressing thing that is true: a failing save first
  // (edits are being lost), then live editing having stopped, otherwise nothing. The
  // banner is aria-live, so it is only written when what it says changes: rewriting the
  // same words (every good autosave clears the save error) would have them read again.
  renderBanner() {
    if (!this.hasBannerTarget) return
    let text = ""
    if (this.saveFailing) text = SAVE_ERROR_MESSAGE
    else if (this.liveEditingStopped) text = LIVE_EDITING_STOPPED_MESSAGE
    const active = text !== ""
    if (this.bannerTarget.textContent !== text) this.bannerTarget.textContent = text
    if (this.bannerTarget.classList.contains("is-active") !== active) {
      this.bannerTarget.classList.toggle("is-active", active)
    }
  }

  scheduleSnapshot() {
    clearTimeout(this._snapshotTimer)
    this._snapshotTimer = setTimeout(() => this.provider?.pushSnapshot(), 4000)
  }

  refreshToolbar() {
    if (!this.editor) return
    this.toolbar?.classList.toggle("is-in-table", this.editor.isActive("table"))
  }

  disconnect() {
    // FD-483: invalidate any in-flight connect() so a mid-download disconnect
    // can't mount an editor afterwards.
    this.connectToken = null
    this.form?.removeEventListener("submit", this.syncOnSubmit)
    this.form?.removeEventListener("reset", this.onFormReset)
    if (this.resetOnSubmitEnd) this.form?.removeEventListener("turbo:submit-end", this.resetOnSubmitEnd)
    if (this.onDocMorph) {
      document.removeEventListener("turbo:morph", this.onDocMorph) // FD-552
      document.removeEventListener("turbo:frame-render", this.onDocMorph) // FD-1186
    }
    clearTimeout(this._autosaveTimer)
    clearTimeout(this._snapshotTimer)
    this._autosaveAbort?.abort()
    this.editor?.destroy()
    this.editor = null
    // FD-267: tear down collab transport + shared doc.
    this.provider?.destroy()
    this.provider = null
    this.ydoc?.destroy()
    this.ydoc = null
  }

  sync(editor) {
    // FD-526: an in-flight paste-upload placeholder must never reach the hidden
    // input (submit sync and collab autosave both come through here).
    this.inputTarget.value = editor.isEmpty ? "" : this.kit.stripPendingUploads(editor.getHTML())
  }

  // FD-438: empty the editor (and the hidden Action Text input) after a comment
  // posts, then re-focus so the next comment can be typed straight away. clearContent
  // emits an update which re-syncs the input to "", but we sync explicitly to be safe.
  resetComposer() {
    if (!this.editor) return
    this.editor.commands.clearContent(true)
    this.sync(this.editor)
    // FD-1190: the composer was just used, so it is on screen; don't scroll for the refocus.
    this.editor.commands.focus(null, { scrollIntoView: false })
  }

  // Discard the draft on a form reset: restore the editor — and, through sync, the hidden
  // input the reset itself can't restore — to the HTML the editor was seeded from. (After a
  // 422 the server deliberately re-renders the SUBMITTED value, so that becomes the seed and
  // the surface stays consistent with the rest of the form.)
  resetToDefault() {
    if (!this.editor || this.collab) return
    this.editor.commands.setContent(this.pristineHtml || "")
    this.sync(this.editor)
  }

  run(command) {
    if (!this.editor) return // FD-483: toolbar clicked before the editor chunk loaded
    this.editor.chain().focus()[command]().run()
  }

  bold(e) { e.preventDefault(); this.run("toggleBold") }
  italic(e) { e.preventDefault(); this.run("toggleItalic") }
  strike(e) { e.preventDefault(); this.run("toggleStrike") }
  bulletList(e) { e.preventDefault(); this.run("toggleBulletList") }
  orderedList(e) { e.preventDefault(); this.run("toggleOrderedList") }
  blockquote(e) { e.preventDefault(); this.run("toggleBlockquote") }
  codeBlock(e) { e.preventDefault(); this.run("toggleCodeBlock") }
  undo(e) { e.preventDefault(); this.run("undo") }
  redo(e) { e.preventDefault(); this.run("redo") }

  heading(e) {
    e.preventDefault()
    if (!this.editor) return
    this.editor.chain().focus().toggleHeading({ level: e.params.level }).run()
  }

  link(e) {
    e.preventDefault()
    if (!this.editor) return
    const previous = this.editor.getAttributes("link").href
    const url = window.prompt("Link URL", previous || "https://")
    if (url === null) return
    if (url === "") {
      this.editor.chain().focus().extendMarkRange("link").unsetLink().run()
    } else {
      this.editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run()
    }
  }

  // FD-210: table editing (the buttons are revealed only when inside a table).
  addRow(e) { e.preventDefault(); this.run("addRowAfter") }
  addColumn(e) { e.preventDefault(); this.run("addColumnAfter") }
  deleteRow(e) { e.preventDefault(); this.run("deleteRow") }
  deleteColumn(e) { e.preventDefault(); this.run("deleteColumn") }
  deleteTable(e) { e.preventDefault(); this.run("deleteTable") }

  // FD-211/FD-598: file upload — toolbar picker; paste/drop are wired in editorProps.
  // Accepts images, PDF, and Excel; the server is the final gate.
  image(e) {
    e.preventDefault()
    if (!this.editor) return
    const input = document.createElement("input")
    input.type = "file"
    input.accept = ALLOWED_UPLOAD_ACCEPT
    input.addEventListener("change", () => {
      Array.from(input.files || []).forEach((file) => this.uploadFile(file))
    })
    input.click()
  }

  handleFiles(fileList, event) {
    const files = Array.from(fileList || []).filter((f) => this.isUploadable(f))
    if (!files.length) return false
    event.preventDefault()
    files.forEach((file) => this.uploadFile(file))
    return true
  }

  // FD-598: images, plus the allowed non-image types by MIME or by extension (Excel is
  // often declared application/octet-stream). The server remains the authoritative gate.
  isUploadable(file) {
    if ((file.type || "").startsWith("image/")) return true
    if (ALLOWED_NON_IMAGE_TYPES.has(file.type)) return true
    return ALLOWED_UPLOAD_EXTENSIONS.test(file.name || "")
  }

  // FD-526: fetch step split from the insert so the paste extension can reuse
  // the exact FD-211/266 upload path (allowlist, size cap, capability gate,
  // throttle) and do its own placeholder-anchored insert. Throws on any
  // failure; `silent` marks the no-upload-URL case (never alerted historically).
  async fetchUpload(file) {
    if (!this.uploadUrlValue) {
      const error = new Error("upload unavailable")
      error.silent = true
      throw error
    }
    const body = new FormData()
    body.append("file", file)
    const res = await fetch(this.uploadUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": this.csrfToken() },
      body,
    })
    if (!res.ok) {
      const error = new Error(`upload failed (${res.status})`)
      error.status = res.status
      throw error
    }
    return res.json()
  }

  async uploadFile(file) {
    let data
    try {
      data = await this.fetchUpload(file)
    } catch (error) {
      if (!error.silent) this.uploadFailed(error.status)
      return
    }
    this.editor?.chain().focus().insertContent({
      type: "actionTextAttachment",
      attrs: {
        raw: {
          sgid: data.sgid,
          "content-type": data.content_type,
          filename: data.filename,
          url: data.url,
          presentation: "gallery",
        },
      },
    }).run()
  }

  uploadFailed(status) {
    window.alert(status === 413 || status === 422
      ? "That file couldn't be uploaded — it may be too large or an unsupported type."
      : "File upload failed. Please try again.")
  }

  // FD-526: one aggregated notice per paste batch (never one alert per image).
  pastedImagesFailed(count) {
    window.alert(count === 1
      ? "A pasted image couldn't be uploaded — it may be too large or an unsupported type. Its alt text was kept instead."
      : `${count} pasted images couldn't be uploaded — they may be too large or an unsupported type. Their alt text was kept instead.`)
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
